import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Dive } from '../types/Dive';
import { Box, Typography, Paper } from '@mui/material';
import { OptionsManager, formatDateTime } from '../utils/optionsManager';
import 'leaflet/dist/leaflet.css';

// Fix for default markers not showing up in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface DiveLocationMapProps {
  dives: Dive[];
  height?: number;
}

// Component to fit map bounds to markers
const FitBounds: React.FC<{ dives: Dive[] }> = ({ dives }) => {
  const map = useMap();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Wait for map to be fully initialized
    if (!map || !map.getContainer()) return;

    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (!isInitialized || !map || !map.getContainer()) return;

    const validDives = dives.filter(dive => dive.latitude && dive.longitude);
    
    if (validDives.length === 0) return;

    try {
      if (validDives.length === 1) {
        // If only one dive, center on it
        const dive = validDives[0];
        map.setView([dive.latitude!, dive.longitude!], 10, { animate: false });
      } else {
        // If multiple dives, fit bounds to include all markers
        const bounds = L.latLngBounds(
          validDives.map(dive => [dive.latitude!, dive.longitude!])
        );
        map.fitBounds(bounds, { padding: [20, 20], animate: false });
      }
    } catch (error) {
      console.error('Error fitting map bounds:', error);
    }
  }, [dives, map, isInitialized]);

  return null;
};

// Create custom dive type icons
const createDiveIcon = (diveType: string): L.DivIcon => {
  const getIconColor = (type: string) => {
    switch (type) {
      case 'Scubadiving': return '#00ACC1';
      case 'Freediving': return '#26A69A';
      case 'Snorkeling': return '#66BB6A';
      case 'Spearfishing': return '#ff9800';
      case 'Mermaiding': return '#ba68c8';
      default: return '#0077BE';
    }
  };

  const color = getIconColor(diveType);
  
  const svgIcon = `
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${color}"/>
      <circle cx="12.5" cy="12.5" r="6" fill="white"/>
      <text x="12.5" y="16" text-anchor="middle" font-size="8" fill="${color}" font-weight="bold">🤿</text>
    </svg>
  `;

  return new L.DivIcon({
    html: svgIcon,
    className: 'custom-dive-marker',
    iconSize: [25, 41],
    iconAnchor: [12.5, 41],
    popupAnchor: [0, -41],
  } as L.DivIconOptions);
};

const DiveLocationMap: React.FC<DiveLocationMapProps> = ({ dives, height = 400 }) => {
  const optionsManager = OptionsManager.getInstance();
  const options = optionsManager.getOptions();
  const [mapKey, setMapKey] = useState(0);
  
  // Filter dives that have valid coordinates
  const divesWithCoordinates = dives.filter(dive => 
    dive.latitude && dive.longitude && 
    !isNaN(dive.latitude) && !isNaN(dive.longitude)
  );

  // Force map remount if dives change significantly
  useEffect(() => {
    setMapKey(prev => prev + 1);
  }, [dives.length]);

  if (divesWithCoordinates.length === 0) {
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)',
          border: '1px solid #b3d9ff',
        }}
      >
        <Box>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            🗺️ No dive locations available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Dive locations will appear here once geocoding is complete
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
      <MapContainer
        key={mapKey}
        style={{ height: `${height}px`, width: '100%' }}
        zoom={10}
        center={[divesWithCoordinates[0].latitude!, divesWithCoordinates[0].longitude!]}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <FitBounds dives={divesWithCoordinates} />
        
        {divesWithCoordinates.map((dive) => (
          <Marker
            key={dive.id}
            position={[dive.latitude!, dive.longitude!]}
            icon={createDiveIcon(dive.diveType)}
          >
            <Popup>
              <Box sx={{ minWidth: { xs: 'min(200px, 70vw)', sm: 200 } }}>
                <Typography variant="h6" gutterBottom>
                  {dive.location || 'Unknown Location'}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {formatDateTime(new Date(dive.startTime), options.display.timeFormat, options.display.dateFormat, dive.latitude, dive.longitude)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Type:</strong> {dive.diveType}
                </Typography>
                <Typography variant="body2">
                  <strong>Max Depth:</strong> {dive.maxDepth}m
                </Typography>
                <Typography variant="body2">
                  <strong>Duration:</strong> {dive.duration}
                </Typography>
                <Typography variant="body2">
                  <strong>Temperature:</strong> {dive.waterTemperature}°C
                </Typography>
              </Box>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </Paper>
  );
};

export default DiveLocationMap;